import { css } from "@emotion/css";
import type { ButtonMenuItem, PageTitleBadge, PrimaryPageAction } from "@octopusdeploy/design-system-components";
import { Badge, Pagination, SearchInput, EmptyStateNoResults } from "@octopusdeploy/design-system-components";
import { CheckCircleIcon, ExperimentIcon, XmarkCircleIcon } from "@octopusdeploy/design-system-icons";
import { TextWithLabel as Text } from "@octopusdeploy/design-system-octopus-components";
import { space, themeTokens } from "@octopusdeploy/design-system-tokens";
import { useInlineStatusMutation, useQuery } from "@octopusdeploy/octopus-react-client";
import type { FeatureToggleResource, TenantResource, FeatureToggleEnvironmentResource, CreateFeatureToggleCommand, PaginatedCollection, ProjectResource, EnvironmentV2Resource, EnvironmentType } from "@octopusdeploy/octopus-server-client";
import { Repository, Permission } from "@octopusdeploy/octopus-server-client";
import { useTrackEvent } from "@octopusdeploy/portal-analytics";
import { links, type QueryParamValuesSetter } from "@octopusdeploy/portal-routes";
import { useCallback, useState } from "react";
import EnvironmentOrMissingChip from "~/areas/projects/components/FeatureToggles/EnvironmentOrMissingChip";
import FeatureToggleTargetsButton from "~/areas/projects/components/FeatureToggles/FeatureToggleTargetsButton";
import { FeatureTogglesDataTableNameCell } from "~/areas/projects/components/FeatureToggles/FeatureTogglesDataTableNameCell";
import { FeatureTogglesOnboarding } from "~/areas/projects/components/FeatureToggles/FeatureTogglesOnboarding";
import { PreviewClientIdentifierDialog } from "~/areas/projects/components/FeatureToggles/PreviewClientIdentifierDialog";
import { RotateClientIdentifierDialog } from "~/areas/projects/components/FeatureToggles/RotateClientIdentifierDialog";
import type { ProjectContextProps } from "~/areas/projects/context/index";
import Dialog from "~/components/Dialog/Dialog";
import { useDialogTrigger } from "~/components/Dialog/DialogTrigger";
import OkDialogLayout from "~/components/DialogLayout/OkDialogLayout";
import { GroupedDataTable } from "~/components/GroupedDataTable/GroupedDataTable";
import { useSpaceAwareNavigation } from "~/components/Navigation/SpaceAwareNavigation/useSpaceAwareNavigation";
import { PageContent } from "~/components/PageContent/PageContent";
import { isAllowed } from "~/components/PermissionCheck/PermissionCheck";
import type { TagIndex } from "~/components/tagSets";
import { getTagIndexForTagSets } from "~/components/tagSets";
import { useDebouncedExternalState } from "~/hooks/useDebouncedExternalState";
interface FilterableFeatureToggleResourcePaginatedCollection extends PaginatedCollection<FeatureToggleResource> {
    IsFiltered: boolean;
}
export interface FeatureTogglesPageData {
    project: ProjectResource;
    featureToggles: FilterableFeatureToggleResourcePaginatedCollection;
    environments: EnvironmentV2Resource[];
    configurableEnvironments: EnvironmentV2Resource[];
    tenants: TenantResource[];
    tenantTagIndex: TagIndex;
}
export interface FeatureTogglesQueryParams {
    toggleName: string;
    page: number | undefined;
    pageSize: number | undefined;
}
const defaultPageSize = 30;
const pageSizeOptions = [defaultPageSize, 50, 100];
function getPagingValues(queryParams: FeatureTogglesQueryParams): {
    currentPage: number;
    pageSize: number;
} {
    const currentPage = queryParams.page ?? 1;
    const pageSize = queryParams.pageSize ?? defaultPageSize;
    return { currentPage, pageSize };
}
export async function featureTogglesPageLoader(repository: Repository, queryParams: FeatureTogglesQueryParams, projectContext: Promise<ProjectContextProps>): Promise<FeatureTogglesPageData> {
    const { state: { model: project }, } = await projectContext;
    const { currentPage, pageSize } = getPagingValues(queryParams);
    const featureTogglesResponsePromise = repository.FeatureToggles.list(project, (currentPage - 1) * pageSize, pageSize, queryParams.toggleName);
    const environmentTypes: EnvironmentType[] = ["Static", "Ephemeral"];
    const environments = repository.Environments.getV2({ spaceId: project.SpaceId, skip: 0, take: Repository.takeAll, type: environmentTypes });
    const configurableEnvironments = repository.Projects.getProjectEnvironments(project.Id, 0, Repository.takeAll, environmentTypes);
    const tenantTagSetsPromise = repository.TagSets.getAllByScopes(["Tenant"]);
    const featureToggles = await featureTogglesResponsePromise;
    // Fetch all the tenants that are configured on toggles. Because we don't need to select tenants on this page, we can avoid tenants.all()
    const tenantIds = featureToggles.Items.flatMap((f) => f.Environments)
        .flatMap((t) => t.TenantIds)
        .reduce((acc, tenantId) => acc.add(tenantId), new Set<string>());
    const excludedTenantIds = featureToggles.Items.flatMap((f) => f.Environments)
        .flatMap((t) => t.ExcludedTenantIds)
        .reduce((acc, tenantId) => acc.add(tenantId), new Set<string>());
    const tenants = await repository.Tenants.all({ ids: [...tenantIds, ...excludedTenantIds] });
    const IsFiltered = !!queryParams.toggleName;
    return {
        environments: (await environments).Items,
        configurableEnvironments: (await configurableEnvironments).Items,
        tenants,
        tenantTagIndex: getTagIndexForTagSets(await tenantTagSetsPromise),
        featureToggles: { ...featureToggles, IsFiltered },
        project,
    };
}
export interface FeatureTogglesPageProps {
    data: FeatureTogglesPageData;
    queryParams: FeatureTogglesQueryParams;
    setQueryParams: QueryParamValuesSetter<FeatureTogglesQueryParams>;
}
export function FeatureTogglesPage({ data, queryParams, setQueryParams }: FeatureTogglesPageProps) {
    const { project, featureToggles: initialFeatureToggles, environments, configurableEnvironments, tenants, tenantTagIndex } = data;
    const [filter, setFilter] = useState<string>(queryParams.toggleName);
    const [debouncedSearch, setDebouncedSearch] = useDebouncedExternalState(queryParams.toggleName, (value) => {
        setFilter(value);
        setQueryParams({ ...queryParams, toggleName: value, page: 1 });
    }, 200);
    const { isOpen: isAddToggleDialogOpen, openDialog: openAddToggleDialog } = useDialogTrigger();
    const { isOpen: isPreviewDialogOpen, openDialog: openPreviewDialog } = useDialogTrigger();
    const { isOpen: isRotateDialogOpen, openDialog: openRotateDialog } = useDialogTrigger();
    const isAllowedToAddToggles = isAllowed({ permission: Permission.FeatureToggleEdit, project: project.Id, environment: "*" });
    const addToggleAction: PrimaryPageAction = {
        type: "button",
        label: "Add Feature Toggle",
        hasPermissions: isAllowedToAddToggles,
        onClick: openAddToggleDialog,
    };
    const previewClientIdentifierAction: ButtonMenuItem = {
        type: "button",
        label: "Preview Client Identifiers",
        onClick: openPreviewDialog,
    };
    const rotateClientIdentifierAction: ButtonMenuItem = {
        type: "button",
        label: "Rotate Client Identifiers",
        onClick: openRotateDialog,
    };
    const onPageChange = useCallback((page: number) => {
        setQueryParams({ ...queryParams, page });
    }, [queryParams, setQueryParams]);
    const onPageSizeChange = useCallback((pageSize: number) => {
        setQueryParams({ ...queryParams, pageSize: pageSize, page: 1 });
    }, [queryParams, setQueryParams]);
    const { currentPage, pageSize } = getPagingValues(queryParams);
    const { result: featureToggles } = useQuery(async (repository) => {
        // Keep name filter locally in case it changes between when the search
        // starts and when we get the results below, so we can work out if
        // there was a filter when we made the request.
        const nameFilter = filter;
        const toggles = await repository.FeatureToggles.list(project, (currentPage - 1) * pageSize, pageSize, nameFilter);
        return { ...toggles, IsFiltered: !!nameFilter };
    }, [project, currentPage, pageSize, filter], "Get feature toggles for project", { initialResult: initialFeatureToggles });
    const showOnboarding = featureToggles.TotalResults === 0 && !featureToggles.IsFiltered;
    const primaryAction = showOnboarding ? undefined : addToggleAction;
    const overflowActions = showOnboarding ? [] : [previewClientIdentifierAction, rotateClientIdentifierAction];
    const titleBadge: PageTitleBadge = { variant: "feature", label: "Alpha", icon: <ExperimentIcon size={20} color={themeTokens.color.badge.icon.feature}/>, tooltipContent: "This feature is in development and may be unstable" };
    return (<PageContent header={{ title: "Feature Toggles", primaryAction, badge: titleBadge, overflowActions }} filters={showOnboarding
            ? undefined
            : {
                inputs: [<SearchInput autoFocus={true} value={debouncedSearch} onChange={setDebouncedSearch} accessibleName={"Search feature toggles by name"} placeholder={"Filter by feature toggle name"}/>],
            }} pagination={showOnboarding
            ? undefined
            : {
                ui: (<Pagination label="Feature Toggles" totalResults={featureToggles.TotalResults} itemsPerPageOptions={pageSizeOptions} selectedItemsPerPage={pageSize} currentPage={currentPage} onPageChange={onPageChange} onPageSizeChange={onPageSizeChange}/>),
                placement: "topAndBottom",
            }}>
            <Dialog open={isAddToggleDialogOpen}>
                <AddFeatureToggleDialog project={project}/>
            </Dialog>
            <Dialog open={isPreviewDialogOpen}>
                <PreviewClientIdentifierDialog project={project} allEnvironments={environments} configurableEnvironments={configurableEnvironments}/>
            </Dialog>
            <Dialog open={isRotateDialogOpen}>
                <RotateClientIdentifierDialog project={project}/>
            </Dialog>
            {showOnboarding ? (<FeatureTogglesOnboarding primaryAction={addToggleAction}/>) : featureToggles.TotalResults === 0 ? (<EmptyStateNoResults />) : (<FeatureToggleTable featureToggles={featureToggles} tenants={tenants} tenantTagIndex={tenantTagIndex} environments={environments} project={project}/>)}
        </PageContent>);
}
interface FeatureToggleEnvironmentRow {
    FeatureToggle: FeatureToggleResource;
    Environment?: FeatureToggleEnvironmentResource;
    RowKey: string;
    IsChild: boolean;
}
interface ToggleStatusBadgeProps {
    isEnabled: boolean;
}
function ToggleStatusBadge({ isEnabled }: ToggleStatusBadgeProps) {
    const styles = {
        wrapper: css({
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
        }),
    };
    const label = isEnabled ? "On" : "Off";
    const variant = isEnabled ? "info" : "neutral";
    const icon = isEnabled ? <CheckCircleIcon size={20}/> : <XmarkCircleIcon size={20}/>;
    return (<div className={styles.wrapper}>
            <Badge size={"medium"} variant={variant} label={label} icon={icon}/>
        </div>);
}
interface ToggleEnvironmentRowProps {
    featureToggle: FeatureToggleResource;
    featureToggleEnvironment: FeatureToggleEnvironmentResource;
    environments: EnvironmentV2Resource[];
    tenants: TenantResource[];
    tenantTagIndex: TagIndex;
    project: ProjectResource;
}
function ToggleEnvironmentRow({ featureToggle, featureToggleEnvironment, environments, tenants, tenantTagIndex, project }: ToggleEnvironmentRowProps) {
    const styles = {
        wrapper: css({
            display: "flex",
            flexWrap: "wrap",
            flexDirection: "row",
            alignItems: "center",
            gap: space[8],
        }),
    };
    return (<div className={styles.wrapper}>
            <EnvironmentOrMissingChip environmentId={featureToggleEnvironment.DeploymentEnvironmentId} environments={environments} noMargin={true}/>
            {featureToggleEnvironment.MinimumVersion && (<>
                    <code>&gt;= {featureToggleEnvironment.MinimumVersion}</code>
                </>)}
            <FeatureToggleTargetsButton project={project} featureToggle={featureToggle} featureToggleEnvironment={featureToggleEnvironment} environments={environments} tenants={tenants} tenantTagIndex={tenantTagIndex}/>
        </div>);
}
interface FeatureToggleTableProps {
    project: ProjectResource;
    featureToggles: FilterableFeatureToggleResourcePaginatedCollection;
    environments: EnvironmentV2Resource[];
    tenants: TenantResource[];
    tenantTagIndex: TagIndex;
}
function FeatureToggleTable({ featureToggles, tenants, tenantTagIndex, environments, project }: FeatureToggleTableProps) {
    function buildToggleRows(featureToggles: FeatureToggleResource[]): FeatureToggleEnvironmentRow[] {
        const rows: FeatureToggleEnvironmentRow[] = [];
        for (const featureToggle of featureToggles) {
            const featureToggleEnvironments = featureToggle.Environments;
            let isChild = false;
            for (const toggle of featureToggleEnvironments) {
                rows.push({
                    FeatureToggle: featureToggle,
                    Environment: toggle,
                    RowKey: `${featureToggle.Id}${toggle.DeploymentEnvironmentId}`,
                    IsChild: isChild,
                });
                isChild = true;
            }
            // IF there are no environments defined, then push a row with just the feature toggle name
            if (featureToggleEnvironments.length === 0) {
                rows.push({
                    FeatureToggle: featureToggle,
                    RowKey: `${featureToggle.Id}`,
                    IsChild: false,
                });
            }
        }
        return rows;
    }
    return (<GroupedDataTable data={buildToggleRows(featureToggles.Items)} columns={[
            {
                title: "Name",
                width: "25%",
                render: (data) => (data.FeatureToggle && !data.IsChild ? <FeatureTogglesDataTableNameCell featureToggle={data.FeatureToggle}/> : <></>),
                isChild: (data) => data.IsChild,
            },
            {
                title: "Status",
                width: "10%",
                render: (data) => (data.Environment ? <ToggleStatusBadge isEnabled={data.Environment.IsEnabled}/> : <div />),
            },
            {
                title: "Environment",
                render: (data) => data.Environment ? (<ToggleEnvironmentRow featureToggle={data.FeatureToggle} featureToggleEnvironment={data.Environment} environments={environments} tenants={tenants} tenantTagIndex={tenantTagIndex} project={project}/>) : (<div />),
            },
        ]} getRowKey={(data) => data.RowKey}/>);
}
interface AddFeatureToggleDialogProps {
    project: Readonly<ProjectResource>;
}
function AddFeatureToggleDialog({ project }: AddFeatureToggleDialogProps) {
    const { navigate } = useSpaceAwareNavigation();
    const [name, setName] = useState("");
    const trackEvent = useTrackEvent();
    const { error: error, execute: add, isExecuting: busy, } = useInlineStatusMutation({
        name: "Add Feature Toggle",
        action: async (repository) => {
            const newFeatureToggle: CreateFeatureToggleCommand = { Name: name, ProjectId: project.Id, Environments: [] };
            const result = await repository.FeatureToggles.create(project, newFeatureToggle);
            trackEvent({ eventName: "Add Feature Toggle" });
            return result;
        },
        afterComplete: async (_, featureToggle) => {
            navigate(links.featureTogglePage.generateUrl({ spaceId: featureToggle.SpaceId, projectSlug: project.Slug, featureToggleSlug: featureToggle.Slug }));
        },
    });
    const errors = error ? [error] : undefined;
    return (<OkDialogLayout title="Add Feature Toggle" okButtonLabel="Save" busy={busy} errors={errors} okButtonDisabled={!name} onOkClick={add}>
            <Text value={name} onChange={(value) => setName(value)} label="Name" autoFocus/>
        </OkDialogLayout>);
}
